// src/pages/InventoryManagement.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomListing from "../components/CustomListing";
import { useDeviceApi } from "../api/device";
import { useOrganizationApi } from "../api/organization";
import ReusableHeader from "../components/ReusableHeader";
import CustomButton from "../components/CustomButton";
import ReusableFilter from "../components/ReusableFilter";
import "../styles/layout.css";
import { formatDate } from "../util/date";
import { toast } from "react-toastify";
import { decodeJwt } from "../util/decodeJwt";
import { useOrderApi } from "../api/orders";
import { Filter, Trash } from "lucide-react";
import CustomDropdown from "../components/CustomDropdown";

export default function InventoryManagement() {
  const { deleteVendorDevice } =useOrderApi();
  const { getAllVendorOrg } = useOrganizationApi()
  const { getAllVendorDevices, getAllDeviceModels } = useDeviceApi();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);

  // Filters
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Organizations
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [rows, setRows] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [searchText, setSearchText] = useState("");

  // From / To Date
const today = new Date();
const currentDate = today.toISOString().split("T")[0]; // "2025-09-26"

const [fromDate, setFromDate] = useState(currentDate);
const [toDate, setToDate] = useState(currentDate);
 // Initialize filterObject WITH default dates
  const [filterObject, setFilterObject] = useState({
    fromDate: currentDate,
    toDate: currentDate,
  });

  // Decode user once
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setUserId(decoded?._id || null);
      setUserRole(decoded?.role?.toLowerCase() || "");
    }
  }, []);


  // Initial load based on today's date
useEffect(() => {
  const fetchTodayDevices = async () => {
    const initialFilters = {
      fromDate,
      toDate,
    };
    setFilterObject(initialFilters); // optional: show filter chips for today
    try {
      const res = await getAllVendorDevices({ limit: 10, offset: 0, ...initialFilters });
      if (res?.success && Array.isArray(res.data)) setRows(res.data);
    } catch {
      toast.error("Failed to fetch today's devices");
    }
  };

  fetchTodayDevices();
}, []); // empty dependency → runs once on mount


  // Fetch organizations
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await getAllVendorOrg({ limit: 100, offset: 0 });
        if (res?.success && Array.isArray(res.data)) {
          setOrganizations(
            res.data.map((o) => ({ value: o._id, label: o.name }))
          );
        }
      } catch {
        toast.error("Failed to load organizations");
      }
    };
    fetchOrgs();
  }, []);

  const handleDelete = async (id) => {
    if (!userId) {
      toast.error("Unauthorized: User not found");
      return;
    }

    if (window.confirm("Are you sure you want to delete this device?")) {
      try {
        await deleteVendorDevice(id, userId);
        toast.success("Device deleted successfully");
        window.location.reload();
      } catch (err) {
        toast.error(err.message || "Failed to delete device");
      }
    }
  };

  const columns = [
    {
      key: "serialNumber",
      label: "BCU Serial Number ",
      render: (row) => row.serialNumber==undefined ?"N/A" : (row.serialNumber=="" ? "N/A":row.serialNumber),
    },
    {
      key: "manufacturerId",
      label: "Machine Serial Number",
      render: (row) => row.manufacturerId==undefined ?"N/A" : (row.manufacturerId=="" ? "N/A":row.manufacturerId),
    },
    {
      key: "macAddress",
      label: "MAC Address",
      render: (row) => row.macAddress==undefined ?"N/A" : (row.macAddress=="" ? "N/A":row.macAddress),
    },
    {
      key: "organization",
      label: "Organization Name",
      render: (row) => row.organization?.address,
    },
    {
      key: "createdBy",
      label: "Created By",
      render: (row) => row.createdBy?.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="action-buttons">
          <CustomButton
            label={<Trash size={16} />}
            className="delete-btn"
            onClick={() => handleDelete(row._id)}
          />
        </div>
      ),
    },
  ];

  const renderActionButtons = () => (
      <div className="header-buttons" style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <input
          type="text"
          placeholder="Search inventory..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
         className="search-input"
        />
      {(userRole === "admin")&& (
        <>
          <CustomButton
            className="header-btn"
            label="New Inward"
            onClick={() => navigate("/inventory-management/inward")}
          />
        </>
      )}
      <CustomButton
        className="header-btn"
        label="New Outward"
        onClick={() => navigate("/inventory-management/outward")}
      />
      <CustomButton
        className="header-btn"
        label={<Filter size={16} className="inline-block mr-1" />}
        onClick={() => setIsFilterOpen(true)}
      />
    </div>
  );

  const fetchWithFilters = async (params = {}) => {
    return await getAllVendorDevices({
      ...params,
      ...filterObject,
      search: searchText || undefined, // include search
    });
  };

  // Debounce search input
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchWithFilters({ limit: 10, offset: 0 })
        .then(res => {
          if (res?.success && Array.isArray(res.data)) setRows(res.data);
        })
        .catch(() => toast.error("Failed to fetch data"));
    }, 500); // 0.5s delay

    return () => clearTimeout(delayDebounce);
  }, [searchText, filterObject]);

  // Apply filters
  const handleApplyFilters = async () => {
    const newFilters = {};

    // Organization
    if (selectedOrg) {
      const org = organizations.find((o) => o.value === selectedOrg);
      newFilters.organizationId = selectedOrg;
      newFilters.organizationName = org ? org.label : selectedOrg;
    }

    // From / To date
    newFilters.fromDate = fromDate;
    newFilters.toDate = toDate;

    setFilterObject(newFilters);
    setIsFilterOpen(false);
    toast.success("Filters applied");

    // Fetch filtered data
    try {
      const res = await getAllVendorDevices({ limit: 10, offset: 0, ...newFilters });
      if (res?.success && Array.isArray(res.data)) setRows(res.data);
    } catch {
      toast.error("Failed to fetch filtered data");
    }
  };

  // Remove filter
  const handleRemoveFilter = async (key) => {
    const updated = { ...filterObject };

    if (key === "organizationId") {
      delete updated.organizationId;
      delete updated.organizationName;
      setSelectedOrg("");
    }

    if (key === "fromDate") {
      delete updated.fromDate;
      setFromDate(currentDate);
    }

    if (key === "toDate") {
      delete updated.toDate;
      setToDate(currentDate);
    }

    setFilterObject(updated);

    // Fetch updated data
    try {
      const res = await getAllVendorDevices({ limit: 10, offset: 0, ...updated });
      if (res?.success && Array.isArray(res.data)) setRows(res.data);
    } catch {
      toast.error("Failed to fetch filtered data");
    }
  };

  // Active filters for chips
  const activeFilters = [];

  if (filterObject.deviceModelNames && filterObject.deviceModelNames.length > 0) {
    activeFilters.push({
      key: "deviceModelIds",
      label: "Model",
      value: filterObject.deviceModelNames.join(", "),
    });
  }

  if (filterObject.organizationName) {
    activeFilters.push({
      key: "organizationId",
      label: "Organization",
      value: filterObject.organizationName,
    });
  }

  if (filterObject.fromDate) {
    activeFilters.push({
      key: "fromDate",
      label: "From Date",
      value: filterObject.fromDate,
    });
  }

  if (filterObject.toDate) {
    activeFilters.push({
      key: "toDate",
      label: "To Date",
      value: filterObject.toDate,
    });
  }

  return (
    <div className="inventoryManagement">
      <ReusableHeader title="Inventory Management" children={renderActionButtons()} />

      {/* Active Filters Chips */}
      <div className="filter-chips-container">
        {activeFilters.map((filter, index) => (
          <div key={index} className="filter-chip">
            {filter.label}: {filter.value}
            <span className="remove-icon" onClick={() => handleRemoveFilter(filter.key)}>
              ✕
            </span>
          </div>
        ))}
      </div>

      <CustomListing
        data={rows}
        setData={setRows}
        fetchData={fetchWithFilters}
        columns={columns}
        pageSize={10}
        showCheckbox={false}   // ✅ THIS HIDES CHECKBOX
      />

      {/* Filter Modal */}
      <ReusableFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Devices"
      >
        <div className="filter-fields">
        <div className="filter-field">
          <label className="filter-label">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="date-input fancy-date"
          />
        </div>

        <div className="filter-field">
          <label className="filter-label">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="date-input fancy-date"
          />
        </div>


          <div className="filter-actions">
            <CustomButton className="apply-btn" label="Apply" onClick={handleApplyFilters} />
            <CustomButton
              className="clear-btn"
              label="Clear"
              onClick={() => {
                setSelectedOrg("");
                setFromDate(currentDate);
                setToDate(currentDate);
                setFilterObject({});
                setIsFilterOpen(false);
                fetchWithFilters({ limit: 10, offset: 0 }).then(res => {
                  if (res?.success && Array.isArray(res.data)) setRows(res.data);
                });
              }}
            />
          </div>
        </div>
      </ReusableFilter>
    </div>
  );
}
